import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../services/api.js';
import type { PendingFollowup } from '../todos/types.js';
import type { FollowupPriority } from '../../utils/followupPriority';

export type TaskAction = 'resolve' | 'keep' | 'update';

export interface TaskDecision {
  action: TaskAction;
  notes?: string;
  priority?: FollowupPriority;
  due_date?: string;
}

export interface FollowupUpdatePayload {
  id: string;
  notes?: string;
  priority?: FollowupPriority;
  due_date?: string;
}

export interface UseOpenTasksResult {
  tasks: PendingFollowup[];
  loading: boolean;
  decisions: Record<string, TaskDecision>;
  setDecision: (taskId: string, decision: TaskDecision) => void;
  resolveIds: string[];
  updates: FollowupUpdatePayload[];
  reload: () => void;
}

function normalizeNotes(value: string | undefined): string {
  return value ?? '';
}

function datePrefix(value: string | undefined): string {
  if (!value) return '';
  return value.substring(0, 10);
}

function seedDecisions(
  fetched: PendingFollowup[],
  prev: Record<string, TaskDecision>,
): Record<string, TaskDecision> {
  const next: Record<string, TaskDecision> = {};
  for (const task of fetched) {
    next[task.id] = prev[task.id] ?? { action: 'resolve' };
  }
  return next;
}

function buildUpdates(
  tasks: PendingFollowup[],
  decisions: Record<string, TaskDecision>,
): FollowupUpdatePayload[] {
  const taskById = new Map(tasks.map((t) => [t.id, t]));
  const result: FollowupUpdatePayload[] = [];

  for (const [taskId, decision] of Object.entries(decisions)) {
    if (decision.action !== 'update') continue;

    const task = taskById.get(taskId);
    if (!task) continue;

    const payload: FollowupUpdatePayload = { id: taskId };
    let changed = false;

    const nextNotes = normalizeNotes(decision.notes ?? task.followup_notes);
    if (nextNotes !== normalizeNotes(task.followup_notes)) {
      payload.notes = nextNotes;
      changed = true;
    }

    const nextPriority = decision.priority ?? task.followup_priority;
    if (nextPriority !== task.followup_priority) {
      payload.priority = nextPriority;
      changed = true;
    }

    const nextDue = datePrefix(decision.due_date ?? task.followup_due_date);
    const currentDue = datePrefix(task.followup_due_date);
    if (nextDue !== currentDue) {
      payload.due_date = nextDue;
      changed = true;
    }

    if (changed) result.push(payload);
  }

  return result;
}

export function useOpenTasks(
  posId: string | null | undefined,
  preselectTaskId?: string | null,
): UseOpenTasksResult {
  const [tasks, setTasks] = useState<PendingFollowup[]>([]);
  const [loading, setLoading] = useState(false);
  const [decisions, setDecisions] = useState<Record<string, TaskDecision>>({});

  const fetchTasks = useCallback(async (targetPosId: string, cancelled: () => boolean) => {
    setLoading(true);
    try {
      const { data } = await api.get<PendingFollowup[]>('/followups', {
        params: { pos_id: targetPosId },
      });
      if (cancelled()) return;
      setTasks(data);
      setDecisions((prev) => seedDecisions(data, prev));
    } catch (error) {
      console.error('Failed to fetch open tasks:', error);
      if (!cancelled()) setTasks([]);
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!posId) {
      setTasks([]);
      setLoading(false);
      setDecisions({});
      return;
    }

    let stale = false;
    const isCancelled = () => stale;
    void fetchTasks(posId, isCancelled);

    return () => {
      stale = true;
    };
  }, [posId, fetchTasks]);

  const setDecision = useCallback((taskId: string, decision: TaskDecision) => {
    setDecisions((prev) => ({ ...prev, [taskId]: decision }));
  }, []);

  const reload = useCallback(() => {
    if (!posId) return;
    void fetchTasks(posId, () => false);
  }, [posId, fetchTasks]);

  const resolveIds = useMemo(() => {
    const ids = Object.entries(decisions)
      .filter(([, d]) => d.action === 'resolve')
      .map(([id]) => id);

    // Deeplinked task without a decision means the list never loaded (offline);
    // an explicit keep/update decision must never be overridden.
    if (preselectTaskId && !decisions[preselectTaskId]) {
      ids.push(preselectTaskId);
    }

    return ids;
  }, [decisions, preselectTaskId]);

  const updates = useMemo(
    () => buildUpdates(tasks, decisions),
    [tasks, decisions],
  );

  return {
    tasks,
    loading,
    decisions,
    setDecision,
    resolveIds,
    updates,
    reload,
  };
}
